import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, User, FileText, Camera, 
  CheckCircle, AlertTriangle, Scan, Loader2, 
  Check, MapPin, Phone, Mail, ChevronRight, X, Trash2,
  Shield, Hash, Calendar, Euro, Building, RotateCcw,
  AlertCircle, ThumbsUp, ThumbsDown, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { compressImage } from '../lib/imageCompress';

const API = `${window.location.origin}/api`;

// Pack images mapping
const PACK_IMAGES = {
  'pack_dj_booth': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/gu1bc6jg_Pack%20%C3%A9cran%20LED%202m2.png',
  'pack_compact': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/vm87efqp_Pack%20%C3%A9cran%20LED%204m2.png',
  'pack_standard': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/nenk0vgh_Pack%20%C3%A9cran%20LED%207m2.png',
  'pack_xl': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/uncbzflz_Pack%20%C3%A9cran%20LED%2015m2.png',
  'default': 'https://customer-assets.emergentagent.com/job_101ab4cd-4d37-431a-b507-ce3ea7ef67e4/artifacts/nenk0vgh_Pack%20%C3%A9cran%20LED%207m2.png'
};

const getPackImageUrl = (packId, packName) => {
  if (packId && PACK_IMAGES[packId]) return PACK_IMAGES[packId];
  const nameLower = (packName || '').toLowerCase();
  if (nameLower.includes('dj') || nameLower.includes('2m')) return PACK_IMAGES['pack_dj_booth'];
  if (nameLower.includes('compact') || nameLower.includes('4m')) return PACK_IMAGES['pack_compact'];
  if (nameLower.includes('standard') || nameLower.includes('7m')) return PACK_IMAGES['pack_standard'];
  if (nameLower.includes('xl') || nameLower.includes('15m')) return PACK_IMAGES['pack_xl'];
  return PACK_IMAGES.default;
};

const CONDITION_OPTIONS = [
  { value: 'Excellent', label: 'Excellent', color: 'text-green-400', bg: 'bg-green-500/20' },
  { value: 'Bon état', label: 'Bon état', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { value: 'Usure normale', label: 'Usure normale', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  { value: 'Endommagé', label: 'Endommagé', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { value: 'Hors service', label: 'Hors service', color: 'text-red-400', bg: 'bg-red-500/20' }
];

const ReturnProcessingPage = () => {
  const { returnId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnData, setReturnData] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [assignedItems, setAssignedItems] = useState([]);
  const [departureInspection, setDepartureInspection] = useState(null);
  
  // Current step: 1 = Scan, 2 = Inspection, 3 = Validation
  const [currentStep, setCurrentStep] = useState(1);
  
  // Scanning state
  const [scanInput, setScanInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannedItems, setScannedItems] = useState([]);
  const scanInputRef = useRef(null);
  
  // Inspection state
  const [inspectionPhotos, setInspectionPhotos] = useState([]);
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [itemConditions, setItemConditions] = useState({});

  // Retenue sur caution (déductions)
  const [deductions, setDeductions] = useState([]); // { description, amount_ht }
  const [refundRemaining, setRefundRemaining] = useState(true);

  const addDeduction = () => setDeductions(prev => [...prev, { description: '', amount_ht: '' }]);
  const updateDeduction = (idx, field, value) => setDeductions(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  const removeDeduction = (idx) => setDeductions(prev => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    fetchReturnDetails();
  }, [returnId]);

  useEffect(() => {
    if (currentStep === 1) {
      // Léger délai pour laisser le rendu se stabiliser (route/chargement) →
      // le champ est prêt à recevoir les bips dès l'ouverture, sans clic.
      const t = setTimeout(() => scanInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [currentStep, loading]);

  const fetchReturnDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/return-requests/${returnId}/details`, { 
        withCredentials: true 
      });
      setReturnData(response.data.return_request);
      setReservation(response.data.reservation);
      setAssignedItems(response.data.assigned_items || []);
      setDepartureInspection(response.data.departure_inspection);

      // Restaure la progression de scan sauvegardée (si la page a été fermée/rechargée)
      const savedScanned = response.data.return_request?.reception_scanned || [];
      if (savedScanned.length) {
        setScannedItems(savedScanned);
      }
      
      // Initialize item conditions
      const initialConditions = {};
      (response.data.assigned_items || []).forEach(item => {
        initialConditions[item.serial_number] = 'Bon état';
      });
      savedScanned.forEach(item => {
        initialConditions[item.serial_number] = item.condition || 'Bon état';
      });
      setItemConditions(initialConditions);
      
    } catch (error) {
      console.error('Error fetching return details:', error);
      toast.error('Erreur lors du chargement du retour');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const saveProgress = async (items) => {
    try {
      await axios.post(`${API}/admin/return-requests/${returnId}/save-progress`,
        { scanned_items: items }, { withCredentials: true });
    } catch (e) {
      console.error('Error saving return progress:', e);
    }
  };

  const handleScanKeyDown = async (e) => {
    if (e.key === 'Enter' && scanInput.trim()) {
      await processScannedSerial(scanInput.trim());
      setScanInput('');
    }
  };

  const processScannedSerial = async (serialNumber) => {
    // Check if already scanned
    if (scannedItems.some(item => item.serial_number === serialNumber)) {
      toast.error(`N° ${serialNumber} déjà scanné !`);
      return;
    }

    // Check if this serial was part of the original shipment.
    // Pour un retour initié par l'admin (commande manuelle) la liste des
    // articles attendus peut être vide : on accepte alors n'importe quel
    // n° de série scanné afin de pouvoir réceptionner et remettre en stock.
    const originalItem = assignedItems.find(item => item.serial_number === serialNumber);
    const noExpectedList = assignedItems.length === 0;
    if (!originalItem && !noExpectedList) {
      toast.error(`N° ${serialNumber} ne fait pas partie de cette commande`);
      return;
    }

    setScanning(true);
    try {
      // Verify the item exists in stock
      const response = await axios.get(`${API}/admin/stock-items/by-serial/${serialNumber}`, {
        withCredentials: true
      });
      
      const stockItem = response.data;
      
      const newItem = {
        serial_number: serialNumber,
        stock_item_id: stockItem.id,
        product_id: stockItem.product_id,
        product_name: stockItem.product_name || originalItem?.product_name || 'Article',
        condition: itemConditions[serialNumber] || 'Bon état',
        original_condition: originalItem?.condition || 'Excellent'
      };
      setScannedItems(prev => {
        const updated = [...prev, newItem];
        saveProgress(updated);
        return updated;
      });

      toast.success(`${stockItem.product_name || originalItem?.product_name || serialNumber} scanné !`);
      
    } catch (error) {
      console.error('Error processing serial:', error);
      toast.error(`Erreur lors du scan de ${serialNumber}`);
    } finally {
      setScanning(false);
      // Refocus après re-render pour que la bippeuse puisse enchaîner sans clic
      setTimeout(() => scanInputRef.current?.focus(), 0);
    }
  };

  const removeScannedItem = (serialNumber) => {
    setScannedItems(prev => {
      const updated = prev.filter(item => item.serial_number !== serialNumber);
      saveProgress(updated);
      return updated;
    });
    toast.info('Article retiré');
  };

  const updateItemCondition = (serialNumber, condition) => {
    setItemConditions(prev => ({ ...prev, [serialNumber]: condition }));
    setScannedItems(prev => {
      const updated = prev.map(item => 
        item.serial_number === serialNumber ? { ...item, condition } : item
      );
      saveProgress(updated);
      return updated;
    });
  };

  const getTotalProgress = () => {
    const total = assignedItems.length;
    const scanned = scannedItems.length;
    const percent = total > 0 ? Math.round((scanned / total) * 100) : 0;
    return { scanned, total, percent };
  };

  const isAllScanned = () => {
    return scannedItems.length >= assignedItems.length;
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    for (const file of files) {
      try {
        const data = await compressImage(file);
        setInspectionPhotos(prev => [...prev, { name: file.name, data, size: Math.round(data.length * 0.75) }]);
      } catch {
        toast.error(`Impossible de charger ${file.name}`);
      }
    }
  };

  const removePhoto = (index) => {
    setInspectionPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (scannedItems.length === 0) {
      toast.error('Veuillez scanner au moins un article');
      return;
    }

    // Retenues sur caution valides (motif + montant HT > 0)
    const validDeductions = deductions
      .filter(d => (d.description || '').trim() && parseFloat(d.amount_ht) > 0)
      .map(d => ({ description: d.description.trim(), quantity: 1, unit_price_ht: parseFloat(d.amount_ht) }));
    const hasDeductions = validDeductions.length > 0;
    const caution = Number(reservation?.caution) || 0;
    const totalDeductionTTC = validDeductions.reduce((s, d) => s + d.unit_price_ht, 0) * 1.20;

    if (hasDeductions && totalDeductionTTC > caution + 0.01) {
      toast.error(`Les retenues (${totalDeductionTTC.toFixed(2)}€ TTC) dépassent la caution (${caution.toFixed(2)}€)`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        scanned_items: scannedItems.map(item => ({
          stock_item_id: item.stock_item_id,
          serial_number: item.serial_number,
          product_id: item.product_id,
          product_name: item.product_name,
          condition: item.condition
        })),
        hold_caution: hasDeductions, // ne pas libérer auto la caution : retenue à appliquer
        return_inspection: {
          photos: inspectionPhotos,
          notes: inspectionNotes,
          items_condition: scannedItems.map(item => ({
            serial_number: item.serial_number,
            product_name: item.product_name,
            condition: item.condition,
            original_condition: item.original_condition
          })),
          inspection_date: new Date().toISOString()
        }
      };

      const resp = await axios.post(`${API}/admin/return-requests/${returnId}/process`, payload, {
        withCredentials: true
      });

      // Étape retenue sur caution (si renseignée)
      if (hasDeductions) {
        try {
          const dedResp = await axios.post(
            `${API}/admin/reservations/${reservation.id}/deposit-deduction-invoice`,
            { line_items: validDeductions, notes: inspectionNotes || null, refund_remaining: refundRemaining },
            { withCredentials: true }
          );
          const s = dedResp.data?.summary || {};
          const refunded = dedResp.data?.refund?.amount_refunded;
          toast.success(
            `Retour traité — retenue de ${Number(s.retenue_ttc || totalDeductionTTC).toFixed(2)}€ TTC appliquée` +
            (refunded ? `, solde de ${Number(refunded).toFixed(2)}€ remboursé au client` : ' (facture de retenue envoyée)')
          );
        } catch (dedErr) {
          console.error('Deduction error:', dedErr);
          toast.error(dedErr.response?.data?.detail || 'Retour traité mais échec de la retenue sur caution. À finaliser depuis le dashboard.');
        }
        navigate('/admin');
        return;
      }

      const cr = resp.data?.caution_release;
      if (cr?.status === 'success') {
        toast.success(`Retour traité — caution de ${Number(cr.amount_refunded).toFixed(2)}€ remboursée automatiquement`);
      } else if (cr?.status === 'review_required') {
        toast.success('Retour traité — caution en attente (litige à arbitrer depuis le dashboard)');
      } else {
        toast.success('Retour traité avec succès !');
      }
      navigate('/admin');
    } catch (error) {
      console.error('Error processing return:', error);
      if (error.response?.status === 413 || (!error.response && error.request)) {
        toast.error('Requête trop volumineuse (photos trop lourdes). Supprimez quelques photos puis réessayez.');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getPackImage = () => {
    return getPackImageUrl(reservation?.pack_id, reservation?.pack_name);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#DFFF00]" />
          <span className="text-white">Chargement du retour...</span>
        </div>
      </div>
    );
  }

  if (!returnData || !reservation) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-white">Retour non trouvé</p>
          <Button onClick={() => navigate('/admin')} className="mt-4">
            Retour au dashboard
          </Button>
        </div>
      </div>
    );
  }

  const customer = reservation.customer || {};
  const progress = getTotalProgress();
  const _cautionAmount = Number(reservation?.caution) || 0;
  const _deductionTTC = deductions.reduce((s, d) => s + (parseFloat(d.amount_ht) || 0), 0) * 1.20;
  const deductionOverCaution = _deductionTTC > _cautionAmount + 0.01;

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div className="h-6 w-px bg-slate-700" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">Réception retour</h1>
                  <p className="text-sm text-slate-400">#{returnId?.slice(0, 8)}</p>
                </div>
              </div>
            </div>
            
            {/* Progress */}
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-xs text-slate-500">Articles scannés</p>
                <p className="text-lg font-bold text-[#DFFF00]">{progress.scanned}/{progress.total}</p>
              </div>
              <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#DFFF00] transition-all duration-300"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { id: 1, label: 'Scan articles', icon: Scan },
            { id: 2, label: 'État des lieux', icon: Camera },
            { id: 3, label: 'Validation', icon: Check }
          ].map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  currentStep === step.id 
                    ? 'bg-[#DFFF00] text-black' 
                    : currentStep > step.id
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-slate-800 text-slate-500'
                }`}
              >
                {currentStep > step.id ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <step.icon className="w-5 h-5" />
                )}
                <span className="font-medium">{step.label}</span>
              </button>
              {index < 2 && (
                <ChevronRight className={`w-5 h-5 ${currentStep > step.id ? 'text-green-400' : 'text-slate-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Panel - Customer & Order Info */}
          <div className="space-y-6">
            {/* Customer Card */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="bg-slate-800/50 px-4 py-3 flex items-center gap-2">
                <User className="w-4 h-4 text-[#DFFF00]" />
                <span className="font-medium text-white text-sm">Client</span>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-white font-semibold">{customer.firstName} {customer.lastName}</p>
                {customer.company && (
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Building className="w-4 h-4" />
                    <span>{customer.company}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Mail className="w-4 h-4" />
                  <span>{customer.email}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>{customer.phone || '-'}</span>
                </div>
              </div>
            </div>

            {/* Pack Info */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="bg-slate-800/50 px-4 py-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-[#DFFF00]" />
                <span className="font-medium text-white text-sm">Pack retourné</span>
              </div>
              <div className="p-4">
                <div className="flex gap-4">
                  <img 
                    src={getPackImage()}
                    alt={reservation.pack_name}
                    className="w-20 h-20 rounded-lg object-cover bg-slate-800"
                  />
                  <div>
                    <p className="font-semibold text-white">{reservation.pack_name}</p>
                    <p className="text-sm text-slate-400">{reservation.pack_surface}</p>
                    <p className="text-[#DFFF00] font-bold mt-1">{formatPrice(reservation.monthly_price)}/mois</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Return Info */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="bg-slate-800/50 px-4 py-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#DFFF00]" />
                <span className="font-medium text-white text-sm">Infos retour</span>
              </div>
              <div className="p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Demande créée</span>
                  <span className="text-white">{formatDate(returnData.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">N° suivi</span>
                  <span className="text-white font-mono">{returnData.tracking_number || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Caution</span>
                  <span className="text-[#DFFF00] font-bold">{formatPrice(reservation.caution)}</span>
                </div>
              </div>
            </div>

            {/* Expected Items */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
              <div className="bg-slate-800/50 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-[#DFFF00]" />
                  <span className="font-medium text-white text-sm">Articles attendus</span>
                </div>
                <span className="text-xs text-slate-500">{assignedItems.length} articles</span>
              </div>
              <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                {assignedItems.map((item, idx) => {
                  const isScanned = scannedItems.some(s => s.serial_number === item.serial_number);
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        isScanned ? 'bg-green-500/10 border border-green-500/30' : 'bg-slate-800/50'
                      }`}
                    >
                      <div>
                        <p className="text-sm text-white">{item.product_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{item.serial_number}</p>
                      </div>
                      {isScanned ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-slate-600" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Panel - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Scan */}
            {currentStep === 1 && (
              <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                <div className="bg-slate-800/50 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Scan className="w-5 h-5 text-[#DFFF00]" />
                    <span className="font-semibold text-white">Scanner les articles retournés</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isAllScanned() ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {progress.scanned}/{progress.total} scannés
                  </span>
                </div>

                <div className="p-6">
                  {/* Scan Input */}
                  <div className="relative mb-6">
                    <Input
                      ref={scanInputRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyDown={handleScanKeyDown}
                      placeholder="Scanner ou saisir le N° de série..."
                      className="bg-slate-800 border-slate-700 text-white h-14 pl-12 text-lg font-mono"
                      readOnly={scanning}
                      onBlur={(e) => {
                        // Garde le focus pour la bippeuse, SAUF si l'opérateur clique
                        // volontairement sur un autre contrôle (select état, corbeille, bouton…)
                        if (currentStep !== 1) return;
                        const to = e.relatedTarget;
                        if (to && to.closest && to.closest('button, select, input, textarea, a, [role="button"]')) return;
                        setTimeout(() => scanInputRef.current?.focus(), 50);
                      }}
                    />
                    <Scan className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
                      scanning ? 'text-[#DFFF00] animate-pulse' : 'text-slate-500'
                    }`} />
                    {scanning && (
                      <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#DFFF00] animate-spin" />
                    )}
                  </div>

                  {/* Scanned Items */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-slate-400">Articles scannés</h4>
                    {scannedItems.length === 0 ? (
                      <div className="text-center py-8 bg-slate-800/30 rounded-lg border border-dashed border-slate-700">
                        <Scan className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                        <p className="text-slate-500">Scannez les articles retournés</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {scannedItems.map((item) => (
                          <div 
                            key={item.serial_number}
                            className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                              <div>
                                <p className="text-sm text-white font-medium">{item.product_name}</p>
                                <p className="text-xs text-slate-400 font-mono">{item.serial_number}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={item.condition}
                                onChange={(e) => updateItemCondition(item.serial_number, e.target.value)}
                                className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1"
                              >
                                {CONDITION_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeScannedItem(item.serial_number)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Missing Items Alert */}
                  {!isAllScanned() && scannedItems.length > 0 && (
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 font-medium text-sm">Articles manquants</p>
                        <p className="text-xs text-slate-400">
                          {progress.total - progress.scanned} article(s) non encore scanné(s)
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Continue Button */}
                  <div className="mt-6 flex justify-end">
                    <Button
                      onClick={() => setCurrentStep(2)}
                      disabled={scannedItems.length === 0}
                      className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6"
                    >
                      Continuer vers l'état des lieux
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Inspection */}
            {currentStep === 2 && (
              <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                <div className="bg-slate-800/50 px-6 py-4 flex items-center gap-3">
                  <Camera className="w-5 h-5 text-[#DFFF00]" />
                  <span className="font-semibold text-white">État des lieux retour</span>
                </div>

                <div className="p-6 space-y-6">
                  {/* Comparison with departure */}
                  {departureInspection && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className="text-blue-400 font-medium text-sm">État des lieux départ disponible</span>
                      </div>
                      <p className="text-xs text-slate-400">
                        Vous pouvez comparer l'état actuel avec l'état au départ.
                      </p>
                    </div>
                  )}

                  {/* Items Condition Summary */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-400 mb-3">État des articles</h4>
                    <div className="space-y-2">
                      {scannedItems.map((item) => {
                        const conditionInfo = CONDITION_OPTIONS.find(c => c.value === item.condition);
                        const hasChanged = item.original_condition !== item.condition;
                        return (
                          <div 
                            key={item.serial_number}
                            className={`p-3 rounded-lg border ${
                              hasChanged 
                                ? 'bg-orange-500/10 border-orange-500/30' 
                                : 'bg-slate-800/50 border-slate-700'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-white font-medium">{item.product_name}</p>
                                <p className="text-xs text-slate-500 font-mono">{item.serial_number}</p>
                              </div>
                              <div className="text-right">
                                <select
                                  value={item.condition}
                                  onChange={(e) => updateItemCondition(item.serial_number, e.target.value)}
                                  className={`bg-slate-800 border text-sm rounded px-3 py-1 ${conditionInfo?.color || 'text-white'} border-slate-700`}
                                >
                                  {CONDITION_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                                {hasChanged && (
                                  <p className="text-xs text-orange-400 mt-1">
                                    Départ: {item.original_condition}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Photos */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Photos de l'état</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {inspectionPhotos.map((photo, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={photo.data} 
                            alt={photo.name}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                      <label className="h-24 border-2 border-dashed border-slate-700 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#DFFF00]/50 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                        <Camera className="w-6 h-6 text-slate-500" />
                      </label>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Observations</h4>
                    <textarea
                      value={inspectionNotes}
                      onChange={(e) => setInspectionNotes(e.target.value)}
                      placeholder="Notes sur l'état général du matériel, dommages constatés, pièces manquantes..."
                      className="w-full h-32 bg-slate-800 border border-slate-700 rounded-lg p-3 text-white text-sm resize-none"
                    />
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                      className="border-slate-700 text-slate-300"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour au scan
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(3)}
                      className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-6"
                    >
                      Continuer vers validation
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Validation */}
            {currentStep === 3 && (
              <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                <div className="bg-slate-800/50 px-6 py-4 flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#DFFF00]" />
                  <span className="font-semibold text-white">Validation du retour</span>
                </div>

                <div className="p-6 space-y-6">
                  {/* Summary */}
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-4">
                    <h4 className="font-medium text-white">Récapitulatif</h4>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Articles scannés</p>
                        <p className="text-white font-medium">{scannedItems.length} / {assignedItems.length}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Photos prises</p>
                        <p className="text-white font-medium">{inspectionPhotos.length}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Articles en bon état</p>
                        <p className="text-green-400 font-medium">
                          {scannedItems.filter(i => ['Excellent', 'Bon état'].includes(i.condition)).length}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Articles endommagés</p>
                        <p className={scannedItems.filter(i => ['Endommagé', 'Hors service'].includes(i.condition)).length > 0 ? 'text-red-400' : 'text-green-400'}>
                          {scannedItems.filter(i => ['Endommagé', 'Hors service'].includes(i.condition)).length}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Warnings */}
                  {!isAllScanned() && (
                    <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-orange-400 font-medium">Attention</p>
                        <p className="text-sm text-slate-400">
                          {assignedItems.length - scannedItems.length} article(s) n'ont pas été scannés. 
                          Ils seront marqués comme non retournés.
                        </p>
                      </div>
                    </div>
                  )}

                  {scannedItems.some(i => ['Endommagé', 'Hors service'].includes(i.condition)) && (
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 font-medium">Dommages constatés</p>
                        <p className="text-sm text-slate-400">
                          Des articles ont été marqués comme endommagés. 
                          Vous pourrez déduire les réparations de la caution après validation.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Retenue sur caution */}
                  {(() => {
                    const caution = Number(reservation?.caution) || 0;
                    const totalHT = deductions.reduce((s, d) => s + (parseFloat(d.amount_ht) || 0), 0);
                    const tva = totalHT * 0.20;
                    const totalTTC = totalHT + tva;
                    const solde = Math.max(caution - totalTTC, 0);
                    const overCaution = totalTTC > caution + 0.01;
                    return (
                      <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-4" data-testid="caution-deduction-block">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#DFFF00]" />
                            <h4 className="font-medium text-white">Retenue sur caution</h4>
                          </div>
                          <span className="text-sm text-slate-400">Caution : <span className="text-[#DFFF00] font-bold">{formatPrice(caution)}</span></span>
                        </div>
                        <p className="text-xs text-slate-500">Ajoutez une retenue (flight case abîmé, LED HS, câble manquant, nettoyage…). Montants saisis <strong>HT</strong>, TVA 20 % ajoutée automatiquement. Le solde de la caution est remboursé au client.</p>

                        {deductions.map((d, idx) => (
                          <div key={idx} className="flex gap-2 items-start" data-testid={`deduction-row-${idx}`}>
                            <Input
                              value={d.description}
                              onChange={(e) => updateDeduction(idx, 'description', e.target.value)}
                              placeholder="Motif (ex. Flight case abîmé, LED HS...)"
                              className="bg-slate-800 border-slate-700 text-white flex-1"
                              data-testid={`deduction-desc-${idx}`}
                            />
                            <div className="relative w-32">
                              <Input
                                type="number" min="0" step="0.01"
                                value={d.amount_ht}
                                onChange={(e) => updateDeduction(idx, 'amount_ht', e.target.value)}
                                placeholder="Montant HT"
                                className="bg-slate-800 border-slate-700 text-white pr-7"
                                data-testid={`deduction-amount-${idx}`}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">€</span>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => removeDeduction(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`remove-deduction-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}

                        <Button variant="outline" onClick={addDeduction} className="border-slate-600 text-slate-300 hover:bg-slate-700 w-full" data-testid="add-deduction-btn">
                          <Plus className="w-4 h-4 mr-2" />Ajouter une retenue
                        </Button>

                        {deductions.length > 0 && (
                          <div className="border-t border-slate-700 pt-3 space-y-1 text-sm">
                            <div className="flex justify-between text-slate-400"><span>Total retenue HT</span><span>{formatPrice(totalHT)}</span></div>
                            <div className="flex justify-between text-slate-400"><span>TVA 20 %</span><span>{formatPrice(tva)}</span></div>
                            <div className="flex justify-between text-white font-medium"><span>Total retenue TTC</span><span>{formatPrice(totalTTC)}</span></div>
                            <div className={`flex justify-between font-bold ${overCaution ? 'text-red-400' : 'text-green-400'}`}>
                              <span>Solde caution remboursé</span><span>{overCaution ? 'Dépassement !' : formatPrice(solde)}</span>
                            </div>
                            {overCaution && <p className="text-xs text-red-400">La retenue dépasse le montant de la caution.</p>}
                            <label className="flex items-center gap-2 pt-2 text-slate-300 cursor-pointer">
                              <input type="checkbox" checked={refundRemaining} onChange={(e) => setRefundRemaining(e.target.checked)} className="accent-[#DFFF00]" data-testid="refund-remaining-checkbox" />
                              <span className="text-xs">Rembourser automatiquement le solde ({formatPrice(solde)}) au client</span>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Validation Info */}
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <ThumbsUp className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-green-400 font-medium">Prêt pour validation</p>
                        <p className="text-sm text-slate-400">
                          En validant, les articles seront remis en stock et marqués disponibles.
                          {deductions.some(d => (d.description||'').trim() && parseFloat(d.amount_ht) > 0)
                            ? ' Une facture de retenue sera générée et envoyée au client, et le solde de la caution lui sera remboursé.'
                            : ' La caution sera remboursée automatiquement (sauf litige) ou gérée depuis le dashboard.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-between pt-4 border-t border-slate-800">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(2)}
                      className="border-slate-700 text-slate-300"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Modifier l'état des lieux
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting || deductionOverCaution}
                      className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold px-8"
                      data-testid="validate-return-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Traitement...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Valider le retour
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReturnProcessingPage;
